import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@lms/ui';
import { SafeHtml } from '@/components/SafeHtml';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api } from '@/lib/api';
import { authenticatedFetch } from '@/lib/authenticated-fetch';
import { userOriginBack } from '@/lib/user-detail-navigation';

interface ContractDetail {
  id: string;
  code: string;
  status: string;
  generatedAt: string;
  acceptedAt?: string | null;
  studentName: string;
  courseName: string;
  templateTitle: string;
  renderedHtml: string;
}

const STATUS_LABELS: Record<string, string> = {
  pending_acceptance: 'Pendiente de aceptación',
  accepted: 'Aceptado',
  generated: 'Generado',
};

export function ContractShowPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const back = userOriginBack(searchParams, '/contratos', 'Volver a Contratos generados');

  const { data, isLoading, error } = useQuery({
    queryKey: ['contracts', id],
    queryFn: () => api<ContractDetail>(`/contracts/${id}`),
    enabled: Boolean(id),
  });

  async function downloadPdf() {
    if (!id) return;
    const response = await authenticatedFetch(`/api/contracts/${id}/pdf`);
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `contrato-${data?.code ?? id}.pdf`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  if (isLoading) {
    return (
      <div className="lms-admin-contracts lms-site-admin-hub-page font-sans">
        <Loader label="Cargando contrato…" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="lms-admin-contracts lms-site-admin-hub-page font-sans">
        <p className="text-red-700">No se pudo cargar el contrato.</p>
      </div>
    );
  }

  return (
    <div className="lms-admin-contracts lms-site-admin-hub-page font-sans">
      <header className="mb-6 flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-4">
          <HubBackLink to={back.to} label={back.label} />
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">{data.templateTitle}</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              {data.studentName} · {data.courseName} · {data.code}
            </p>
            <p className="mt-1 max-w-2xl text-sm text-dash-on-surface-variant">
              Estado: {STATUS_LABELS[data.status] ?? data.status}
              {data.acceptedAt ? ` · Aceptado el ${new Date(data.acceptedAt).toLocaleDateString('es-ES')}` : ''}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-dash-primary px-4 py-2 text-sm font-medium text-dash-primary transition-colors hover:bg-brand-50"
            onClick={() => window.print()}
          >
            <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
              print
            </span>
            Imprimir
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-dash-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90"
            onClick={() => void downloadPdf()}
          >
            <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
              download
            </span>
            Descargar PDF
          </button>
        </div>
      </header>

      <article className="contracts-print-area lms-credentials-glass-panel rounded-2xl p-8 shadow-sm">
        <SafeHtml html={data.renderedHtml} className="prose max-w-none" />
      </article>
    </div>
  );
}
